import React, { type ComponentProps, useEffect, useMemo, useState } from 'react';

import { graphql, useFragment } from 'react-relay';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { AgentCard, type AgentCardProps } from '@atlassian/agent-profile-card/card';
import {
	type ExternalAgentInvokedByInput,
	ExternalAgentProfileCard,
} from '@atlassian/agent-profile-card/external-card';
import { IdentityUserAri, PlatformSiteAri } from '@atlassian/ari';

import type {
	CustomerCardDataLoader,
	HierarchyData,
	HierarchyPerson,
	PersonProfileData,
} from '../types';
import type {
	UserProfileCardRelay_query$data,
	UserProfileCardRelay_query$key,
} from './__generated__/UserProfileCardRelay_query.graphql';
import { CardError } from './components/CardError';
import { CardPopupWrapper } from './components/CardPopupWrapper';
import { InvokingUserProfileCardTrigger } from './components/InvokingUserProfileCardTrigger';
import { getProfileCardKind } from './getProfileCardKind';
import { profileQueryAccountId } from './profileQueryAccountId';
import { UserProfileCard } from './UserProfileCard';

type UserProfileCardPassThroughProps = Pick<
	ComponentProps<typeof UserProfileCard>,
	'cloudId' | 'testId' | 'isLoading' | 'actions' | 'viewerId' | 'isReduced'
>;

type ActivityObjectData = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<UserProfileCardRelay_query$data['activity']>['workedOn']
		>['edges'][number]
	>['node']
>['object']['data'];

/**
 * Derives a canonical URL for a recent-activity object, covering every
 * URL-bearing activity type returned by the full profile-page query.
 * Returns a single-element array (for `flatMap`) or an empty array when the
 * object type has no resolvable URL.
 */
function extractActivityUrl(objectData: ActivityObjectData | null | undefined): string[] {
	if (!objectData) {
		return [];
	}
	switch (objectData.__typename) {
		case 'ConfluencePage':
		case 'ConfluenceBlogPost':
		case 'ConfluenceWhiteboard':
		case 'ConfluenceDatabase':
		case 'ConfluenceEmbed':
		case 'ConfluenceFooterComment':
		case 'ConfluenceInlineComment':
			return objectData.links?.base && objectData.links?.webUi
				? [`${objectData.links.base}${objectData.links.webUi}`]
				: [];
		case 'JiraIssue':
		case 'JiraPlatformComment':
		case 'JiraServiceManagementComment':
		case 'DevOpsDocument':
		case 'DevOpsDesign':
			return objectData.webUrl ? [objectData.webUrl] : [];
		case 'TrelloBoard':
		case 'TrelloCard':
			return objectData.trelloUrl ? [objectData.trelloUrl] : [];
		case 'TownsquareGoal':
		case 'TownsquareProject':
		case 'TownsquareComment':
		case 'ExternalDocument':
		case 'DevOpsPullRequestDetails':
		case 'ExternalPullRequest':
		case 'ExternalRemoteLink':
		case 'MercuryFocusArea':
		case 'MercuryPortfolio':
		case 'RadarPosition':
		case 'RadarView':
		case 'CompassComponent':
			return objectData.url ? [objectData.url] : [];
		case 'BitbucketPullRequest':
			return objectData.href ? [objectData.href] : [];
		case 'LoomVideo':
			return objectData.loomUrl ? [objectData.loomUrl] : [];
		case 'AssetsObject':
		case 'AssetsObjectType':
		case 'AssetsSchema':
			return objectData.links?.web ? [objectData.links.web] : [];
		default:
			return [];
	}
}

/**
 * Computes the de-duplication key for a recent-activity object, mirroring the
 * full profile page's `deduplicateId` behaviour:
 * - Confluence comments de-duplicate on their container (page/blog/whiteboard),
 *   so multiple comments on the same page collapse to a single entry
 *   (equivalent to `deduplicateId: ['container.id']`).
 * - Jira comments de-duplicate on their parent issue
 *   (equivalent to `deduplicateId: ['issue.id']`).
 * - Everything else de-duplicates on its own URL
 *   (equivalent to `deduplicateId: ['objectId']`).
 *
 * Falls back to the object's own URL when the container/issue is unavailable.
 */
function getActivityDedupeKey(objectData: ActivityObjectData | null | undefined): string {
	const ownUrl = extractActivityUrl(objectData)[0] ?? '';
	if (!objectData) {
		return ownUrl;
	}
	switch (objectData.__typename) {
		case 'ConfluenceFooterComment':
		case 'ConfluenceInlineComment': {
			const container = objectData.container;
			const containerUrl =
				container && 'links' in container && container.links?.base && container.links?.webUi
					? `${container.links.base}${container.links.webUi}`
					: '';
			return containerUrl || ownUrl;
		}
		case 'JiraPlatformComment':
		case 'JiraServiceManagementComment':
			return objectData.issue?.webUrl || ownUrl;
		default:
			return ownUrl;
	}
}

const RECENT_WORK_LINK_LIMIT = 3;

type ActivityCandidate = { product: string; url: string };

/**
 * How many activity entries to request. The card shows at most three links, but entries are
 * dropped before that: types with no resolvable URL, and comments that collapse onto their
 * container. The default over-fetches heavily to survive that; the smaller size trades some of
 * that headroom for a much cheaper request on a surface that fires on hover.
 */
function getRecentWorkPageSize(): number {
	return fg('ptc_user_profile_card_reduce_activity_traffic') ? 15 : 50;
}

/**
 * Picks up to `limit` links spread across products instead of taking the most recent few, which a
 * single busy product (a Jira-heavy week, say) would otherwise fill on its own. Products are
 * visited in order of their most recent activity, taking one link each per pass, so the first pick
 * is still the single most recent item.
 */
function pickDiverseLinks(candidates: ActivityCandidate[], limit: number): string[] {
	const urlsByProduct = new Map<string, string[]>();
	for (const candidate of candidates) {
		const existing = urlsByProduct.get(candidate.product);
		if (existing) {
			existing.push(candidate.url);
		} else {
			urlsByProduct.set(candidate.product, [candidate.url]);
		}
	}

	const queues = Array.from(urlsByProduct.values());
	const picked: string[] = [];
	while (picked.length < limit && queues.some((queue) => queue.length > 0)) {
		for (const queue of queues) {
			const url = queue.shift();
			if (url) {
				picked.push(url);
			}
			if (picked.length === limit) {
				break;
			}
		}
	}
	return picked;
}

type ReportingLines = Extract<
	NonNullable<UserProfileCardRelay_query$data['knowledgeDiscovery']>['reportingLines'],
	{ __typename: 'KnowledgeDiscoveryReportingLines' }
>;
type ReportingLinePerson = NonNullable<ReportingLines['managers']>[number];

/**
 * Maps a knowledge-discovery reporting-line person into the card's `HierarchyPerson`, or `null` when
 * the entry has no account identifier (unusable for rendering/keys).
 */
function mapHierarchyPerson(
	person: ReportingLinePerson | null | undefined,
): HierarchyPerson | null {
	if (!person?.accountIdentifier) {
		return null;
	}
	return {
		accountId: person.accountIdentifier,
		name: person.pii?.name ?? '',
		avatarUrl: person.pii?.picture ?? null,
		jobTitle: person.pii?.extendedProfile?.jobTitle ?? null,
		numberOfDirectReports: person.numberOfDirectReports ?? undefined,
		numberOfTotalReports: person.numberOfTotalReports ?? undefined,
	};
}

/** Maps a reporting-line person list to `HierarchyPerson`, dropping entries without an account id. */
function mapPeople(
	people: ReadonlyArray<ReportingLinePerson> | null | undefined,
): HierarchyPerson[] {
	return (people ?? [])
		.map(mapHierarchyPerson)
		.filter((person): person is HierarchyPerson => person !== null);
}

/**
 * Agent-card config forwarded to `AgentCard`. `accountId` / `cloudId` come from the Relay data.
 * `resourceClient` is optional here (unlike `AgentCardProps`) because it's only consumed by
 * `AgentCard`'s legacy, non-Relay fallback; the default Relay path never reads it.
 */
export type UserProfileCardRelayAgentProps = Omit<
	AgentCardProps,
	'accountId' | 'cloudId' | 'resourceClient'
> & {
	resourceClient?: AgentCardProps['resourceClient'];
};

/**
 * Builds the full site ARI that `reportingLines(siteId:)` requires (it needs an ARI, not the raw
 * cloudId). Returns `''` when the cloudId is missing.
 */
function buildSiteAri(cloudId: string | undefined): string {
	return cloudId ? PlatformSiteAri.create({ siteId: cloudId }).toString() : '';
}

interface UserProfileCardRelayProps extends UserProfileCardPassThroughProps {
	/** Relay fragment ref returned by the query that spreads `...UserProfileCardRelay_query`. */
	queryFragment: UserProfileCardRelay_query$key;
	orgId?: string;
	/** Agent-card config, forwarded to `AgentCard` when the subject is a Rovo agent. Defaults to `{}`; pass custom actions (chat, delete, etc.) to override. */
	agent?: UserProfileCardRelayAgentProps;
	/** Optional person the external agent acted on behalf of. */
	agentInvokedBy?: ExternalAgentInvokedByInput;
	prefilledProfileData?: PersonProfileData;
	/**
	 * @private
	 * @deprecated Temporary Jira portal-only email bridge while AGG omits site-scoped `CustomerUser.email`.
	 */
	customerCardDataLoader?: CustomerCardDataLoader;
}

/**
 * Renders a user profile card from a Relay fragment.
 *
 * The fragment is colocated: it contains the user's base profile (name, avatar,
 * email, extendedProfile) and the supplemental data (manager, third-party
 * integrations, recent work) in one spread, so everything fetches in a single
 * round-trip.
 *
 * Recent-work previews need a `SmartCardProvider` from `@atlaskit/link-provider`
 * in scope. Atlassian product shells provide this by default.
 *
 * @example
 * ```tsx
 * const data = useLazyLoadQuery<MyQuery>(
 *   graphql`
 *     query MyQuery($userId: ID!, $cloudId: String!, $orgId: String!, $viewerId: String!) {
 *       ...UserProfileCardRelay_query @arguments(
 *         userId: $userId, cloudId: $cloudId, orgId: $orgId, viewerId: $viewerId
 *       )
 *     }
 *   `,
 *   buildQueryVariables({ userId, cloudId, orgId, viewerId }),
 * );
 * return <UserProfileCardRelay queryFragment={data} cloudId={cloudId} />;
 * ```
 */
function UserProfileCardRelay({
	queryFragment,
	cloudId,
	orgId,
	testId,
	isLoading,
	agent,
	agentInvokedBy,
	actions,
	viewerId,
	prefilledProfileData,
	customerCardDataLoader,
	isReduced,
}: UserProfileCardRelayProps): React.JSX.Element {
	const data = useFragment<UserProfileCardRelay_query$key>(
		graphql`
			fragment UserProfileCardRelay_query on Query
			@argumentDefinitions(
				userId: { type: "ID!" }
				userIdString: { type: "String!" }
				cloudId: { type: "String!" }
				orgId: { type: "String!" }
				viewerId: { type: "String!" }
				siteAri: { type: "String!" }
				userAri: { type: "ID!" }
				skipKnowledgeDiscovery: { type: "Boolean!", defaultValue: false }
				skipReportingLines: { type: "Boolean!", defaultValue: true }
				skipActivity: { type: "Boolean!", defaultValue: false }
				includeExternalAgentFields: { type: "Boolean!", defaultValue: false }
				collabContextId: { type: "ID", defaultValue: null }
				activityFirst: { type: "Int", defaultValue: 50 }
			) {
				user(accountId: $userId) {
					__typename
					accountId
					accountStatus
					name
					picture
					... on AtlassianAccountUser {
						email
						extendedProfile {
							jobTitle
							department
							location
							organization
						}
					}
					... on CustomerUser {
						email
					}
					... on AppUser {
						appType
						# eslint-disable-next-line @atlassian/relay/unused-fields -- Retain the new AppUser data while product selects which fields to display.
						nickname @include(if: $includeExternalAgentFields)
						extendedProfile @include(if: $includeExternalAgentFields) {
							jobTitle
							aboutMe
						}
						# eslint-disable-next-line @atlassian/relay/unused-fields -- Keep the new raw AppUser field available while product reviews which values belong on the card.
						characteristics @include(if: $includeExternalAgentFields)
					}
				}
				activity @skip(if: $skipActivity) {
					workedOn(
						first: $activityFirst
						filter: { actors: [$userAri], collabContextId: $collabContextId }
					) {
						edges {
							node {
								object {
									product
									data {
										__typename
										... on ConfluencePage {
											links {
												base
												webUi
											}
										}
										... on ConfluenceBlogPost {
											links {
												base
												webUi
											}
										}
										... on ConfluenceWhiteboard {
											links {
												base
												webUi
											}
										}
										... on ConfluenceDatabase {
											links {
												base
												webUi
											}
										}
										... on ConfluenceEmbed {
											links {
												base
												webUi
											}
										}
										... on ConfluenceFooterComment {
											links {
												base
												webUi
											}
											container {
												__typename
												... on ConfluencePage {
													links {
														base
														webUi
													}
												}
												... on ConfluenceBlogPost {
													links {
														base
														webUi
													}
												}
												... on ConfluenceWhiteboard {
													links {
														base
														webUi
													}
												}
											}
										}
										... on ConfluenceInlineComment {
											links {
												base
												webUi
											}
											container {
												__typename
												... on ConfluencePage {
													links {
														base
														webUi
													}
												}
												... on ConfluenceBlogPost {
													links {
														base
														webUi
													}
												}
												... on ConfluenceWhiteboard {
													links {
														base
														webUi
													}
												}
											}
										}
										... on TownsquareGoal {
											url
										}
										... on TownsquareProject {
											url
										}
										... on TownsquareComment {
											url
										}
										... on JiraIssue {
											webUrl
										}
										... on JiraPlatformComment {
											webUrl
											issue {
												webUrl
											}
										}
										... on JiraServiceManagementComment {
											webUrl
											issue {
												webUrl
											}
										}
										... on TrelloBoard {
											trelloUrl: url
										}
										... on TrelloCard {
											trelloUrl: url
										}
										... on DevOpsDocument {
											webUrl: url
										}
										... on ExternalDocument {
											url
										}
										... on DevOpsPullRequestDetails {
											url
										}
										... on ExternalPullRequest {
											url
										}
										... on DevOpsDesign {
											webUrl: url
										}
										... on ExternalRemoteLink {
											url
										}
										... on MercuryFocusArea {
											url
										}
										... on MercuryPortfolio {
											url
										}
										... on RadarPosition {
											url
										}
										... on RadarView {
											url
										}
										... on CompassComponent {
											url
										}
										... on BitbucketPullRequest {
											href
										}
										... on LoomVideo {
											loomUrl: url
										}
										... on AssetsObject {
											links {
												web
											}
										}
										... on AssetsObjectType {
											links {
												web
											}
										}
										... on AssetsSchema {
											links {
												web
											}
										}
									}
								}
							}
						}
					}
				}
				knowledgeDiscovery @skip(if: $skipKnowledgeDiscovery) {
					singleUser(
						orgId: $orgId
						tenantId: $cloudId
						accountId: $userIdString
						requestingUserId: $viewerId
					) @optIn(to: "KnowledgeDiscovery Single User") {
						__typename
						... on KnowledgeDiscoverySingleUser {
							user {
								manager {
									id
									jobTitle
									picture
									name
								}
								thirdPartyIntegrations {
									externalId
									integrationId
									product
								}
							}
						}
					}
					reportingLines(aaid: $userIdString, orgId: $orgId, siteId: $siteAri)
						@skip(if: $skipReportingLines)
						@optIn(to: "KnowledgeDiscovery Get reporting lines") {
						__typename
						... on KnowledgeDiscoveryReportingLines {
							user {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
							managers {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
							reports {
								accountIdentifier
								numberOfDirectReports
								numberOfTotalReports
								pii {
									name
									picture
									extendedProfile {
										jobTitle
									}
								}
							}
						}
					}
				}
			}
		`,
		queryFragment,
	);

	return (
		<UserProfileCardRelayContent
			data={data}
			cloudId={cloudId}
			orgId={orgId}
			testId={testId}
			isLoading={isLoading}
			agent={agent}
			agentInvokedBy={agentInvokedBy}
			actions={actions}
			viewerId={viewerId}
			prefilledProfileData={prefilledProfileData}
			customerCardDataLoader={customerCardDataLoader}
			isReduced={isReduced}
		/>
	);
}

function UserProfileCardRelayContent({
	data,
	cloudId,
	orgId,
	testId,
	isLoading,
	agent = {},
	agentInvokedBy,
	actions,
	viewerId,
	prefilledProfileData,
	customerCardDataLoader,
	isReduced,
}: {
	data: UserProfileCardRelay_query$data;
	cloudId: string;
	orgId?: string;
	testId?: string;
	isLoading?: boolean;
	agent?: UserProfileCardRelayAgentProps;
	agentInvokedBy?: ExternalAgentInvokedByInput;
	prefilledProfileData?: PersonProfileData;
	customerCardDataLoader?: CustomerCardDataLoader;
} & Pick<
	UserProfileCardPassThroughProps,
	'actions' | 'viewerId' | 'isReduced'
>): React.JSX.Element {
	const user = data.user;
	const accountId = user?.accountId ?? '';
	const [customerProfileData, setCustomerProfileData] = useState<{
		accountId: string;
		profileData: Partial<PersonProfileData>;
	} | null>(null);

	useEffect(() => {
		if (
			!accountId ||
			user?.__typename !== 'CustomerUser' ||
			user.email ||
			!customerCardDataLoader
		) {
			return;
		}

		let isCurrent = true;
		void Promise.resolve()
			.then(() => customerCardDataLoader(cloudId, accountId))
			.then((profileData) => {
				if (isCurrent) {
					setCustomerProfileData({ accountId, profileData });
				}
			})
			.catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [accountId, cloudId, customerCardDataLoader, user?.__typename, user?.email]);

	const fallbackProfileData =
		customerProfileData?.accountId === accountId ? customerProfileData.profileData : undefined;

	const rawSingleUser = data.knowledgeDiscovery?.singleUser;
	const knowledgeSingleUser = rawSingleUser && 'user' in rawSingleUser ? rawSingleUser : null;
	const rawManager = knowledgeSingleUser?.user?.manager;

	const rawReportingLines = data.knowledgeDiscovery?.reportingLines;

	const hierarchyData = useMemo((): HierarchyData | undefined => {
		if (!rawReportingLines || !('user' in rawReportingLines)) {
			return undefined;
		}
		const hierarchyUser = mapHierarchyPerson(rawReportingLines.user);
		if (!hierarchyUser) {
			return undefined;
		}
		const managers = mapPeople(rawReportingLines.managers);
		const reports = mapPeople(rawReportingLines.reports);

		return {
			user: hierarchyUser,
			managers: managers.length ? managers : undefined,
			reports: reports.length ? reports : undefined,
		};
	}, [rawReportingLines]);

	const recentWorkData = useMemo(() => {
		const edges = data.activity?.workedOn?.edges ?? [];
		// De-duplicate by the profile page's `deduplicateId` key (comments collapse
		// onto their container/issue), keeping the first occurrence, then resolve
		// each survivor to its display URL.
		const seenKeys = new Set<string>();
		const candidates: ActivityCandidate[] = [];
		for (const edge of edges) {
			const activityObject = edge?.node?.object;
			const displayUrl = extractActivityUrl(activityObject?.data)[0];
			if (!displayUrl) {
				continue;
			}
			const dedupeKey = getActivityDedupeKey(activityObject?.data);
			if (seenKeys.has(dedupeKey)) {
				continue;
			}
			seenKeys.add(dedupeKey);
			candidates.push({ product: activityObject?.product ?? '', url: displayUrl });
		}

		const urls = fg('ptc_user_profile_card_reduce_activity_traffic')
			? pickDiverseLinks(candidates, RECENT_WORK_LINK_LIMIT)
			: candidates.slice(0, RECENT_WORK_LINK_LIMIT).map((candidate) => candidate.url);
		return urls.length ? urls : undefined;
	}, [data.activity?.workedOn?.edges]);

	const appUserJobTitle = user?.__typename === 'AppUser' ? user.extendedProfile?.jobTitle : null;
	const appUserDescription =
		user?.__typename === 'AppUser' ? (user.extendedProfile?.aboutMe ?? undefined) : undefined;
	const appType = user && 'appType' in user ? user.appType : null;
	// AGG's AppUser job titles mark the Atlassian MCP route (graphql-gateway PR #6677).
	// The TWG variant has no marker here; computer control and user API key routes are not
	// identified by these values. Keep the loading and loaded card on the same routing rule.
	const cardKind = getProfileCardKind({
		userType: user?.__typename,
		appType,
		jobTitle: appUserJobTitle,
	});
	const isAgent = cardKind !== 'person';

	const profileData = useMemo(() => {
		if (!user || isAgent) {
			return null;
		}
		const knowledge = knowledgeSingleUser?.user;

		const manager = rawManager?.id
			? {
					id: rawManager.id,
					name: rawManager.name,
					avatarUrl: rawManager.picture,
					jobTitle: rawManager.jobTitle,
				}
			: null;

		const thirdPartyIntegrations = knowledge?.thirdPartyIntegrations
			?.filter(
				(
					integration,
				): integration is { externalId: string; integrationId: string; product: string } =>
					Boolean(integration?.externalId && integration?.integrationId && integration?.product),
			)
			.map((integration) => ({
				externalId: integration.externalId,
				integrationId: integration.integrationId,
				product: integration.product,
			}));

		return {
			accountId: user.accountId,
			accountStatus: user.accountStatus ?? fallbackProfileData?.accountStatus,
			name: user.name ?? fallbackProfileData?.name ?? '',
			pronouns: fallbackProfileData?.pronouns,
			avatarUrl: user.picture ?? fallbackProfileData?.avatarUrl,
			email: user.email || fallbackProfileData?.email,
			jobTitle: user.extendedProfile?.jobTitle ?? fallbackProfileData?.jobTitle,
			department: user.extendedProfile?.department ?? fallbackProfileData?.department,
			location: user.extendedProfile?.location ?? fallbackProfileData?.location,
			localTime: fallbackProfileData?.localTime,
			organization: user.extendedProfile?.organization ?? fallbackProfileData?.organization,
			manager: manager ?? fallbackProfileData?.manager ?? null,
			thirdPartyIntegrations: thirdPartyIntegrations?.length
				? thirdPartyIntegrations
				: (fallbackProfileData?.thirdPartyIntegrations ?? null),
		};
	}, [user, isAgent, knowledgeSingleUser, rawManager, fallbackProfileData]);

	if (!accountId) {
		return <CardError testId={testId} />;
	}

	// Agent cards own their chrome and width. Both agent kinds use the same trigger placement and
	// offset; the popup recalculates after the loading card changes size.
	if (isAgent) {
		if (cardKind === 'external-agent') {
			return (
				<ExternalAgentProfileCard
					accountId={accountId}
					name={user?.name ?? ''}
					iconUrl={user?.picture}
					description={appUserDescription}
					withUser={agentInvokedBy}
					renderInvokingUser={(invoker, content, visibleLabel) =>
						invoker.accountId ? (
							<InvokingUserProfileCardTrigger
								user={{ ...invoker, accountId: invoker.accountId }}
								cloudId={cloudId}
								orgId={orgId}
								viewerId={viewerId}
								visibleLabel={visibleLabel}
							>
								{content}
							</InvokingUserProfileCardTrigger>
						) : (
							content
						)
					}
					via="mcp"
					testId={testId}
				/>
			);
		}

		return (
			<AgentCard
				{...agent}
				accountId={accountId}
				cloudId={cloudId}
				resourceClient={agent.resourceClient as AgentCardProps['resourceClient']}
			/>
		);
	}

	const displayedProfileData =
		isLoading && prefilledProfileData ? prefilledProfileData : profileData;

	if (!displayedProfileData) {
		return <CardError testId={testId} />;
	}

	return (
		<CardPopupWrapper>
			<UserProfileCard
				cloudId={cloudId}
				testId={testId}
				isLoading={isLoading}
				profileData={displayedProfileData}
				canViewProfile={user?.__typename !== 'CustomerUser'}
				recentWorkData={recentWorkData}
				hierarchyData={hierarchyData}
				actions={actions}
				viewerId={viewerId}
				isReduced={isReduced}
			/>
		</CardPopupWrapper>
	);
}

type UserProfileCardQueryParams = {
	/** Account ID of the user whose card to render. */
	userId: string;
	/** Site / tenant cloud ID. Omit to skip the knowledge-discovery section. */
	cloudId?: string;
	/** Organisation ID. Omit to skip the knowledge-discovery section. */
	orgId?: string;
	/** Account ID of the currently logged-in user (the viewer). Omit to skip the knowledge-discovery section. */
	viewerId?: string;
	/** Reduced card (header + manager only) — skips the recent-activity fetch it never renders. */
	isReduced?: boolean;
};

/**
 * Builds the variables for a query that spreads `UserProfileCardRelay_query`.
 *
 * The fragment needs a few extra variables (`userIdString`, `userAri`) that are
 * derived from `userId` to satisfy the GraphQL schema's scalar types. This helper
 * also normalizes an identity user ARI to an account ID for APIs that require one.
 *
 * @example
 * ```tsx
 * const data = useLazyLoadQuery<MyQuery>(
 *   MY_QUERY,
 *   buildQueryVariables({ userId, cloudId, orgId, viewerId }),
 * );
 * ```
 */
type UserProfileCardQueryVariables = {
	userId: string;
	userIdString: string;
	cloudId: string;
	orgId: string;
	viewerId: string;
	siteAri: string;
	userAri: string;
	skipKnowledgeDiscovery: boolean;
	skipReportingLines: boolean;
	skipActivity: boolean;
	collabContextId: string | null;
	activityFirst: number;
	includeExternalAgentFields: boolean;
};

function buildQueryVariables({
	userId,
	cloudId,
	orgId,
	viewerId,
	isReduced,
}: UserProfileCardQueryParams): UserProfileCardQueryVariables {
	const skipKnowledgeDiscovery = !cloudId || !orgId || !viewerId;
	const siteAri = buildSiteAri(cloudId);
	const skipReportingLines = skipKnowledgeDiscovery || !siteAri;
	// The reduced card never renders the recent-activity panel, so don't fetch it.
	// Without a cloudId there is no collab context for Worklens to resolve.
	const skipActivity =
		!!isReduced ||
		(!cloudId &&
			(fg('mycl-493-activity-always-has-collab-context') ||
				fg('mycl-493-bitbucket-provides-collab-context')));
	const isUserAri = IdentityUserAri.check(userId);
	const accountId = profileQueryAccountId(userId);

	return {
		userId: accountId,
		userIdString: accountId,
		cloudId: cloudId ?? '',
		orgId: orgId ?? '',
		viewerId: viewerId ?? '',
		siteAri,
		userAri: isUserAri ? userId : IdentityUserAri.create({ userId }).toString(),
		skipKnowledgeDiscovery,
		skipReportingLines,
		skipActivity,
		collabContextId: cloudId ? PlatformSiteAri.create({ siteId: cloudId }).toString() : null,
		activityFirst: getRecentWorkPageSize(),
		includeExternalAgentFields: isExperimentEnabled('cc_external_agent_profile_card'),
	};
}

export { UserProfileCardRelay, buildQueryVariables };
export type { UserProfileCardRelayProps, UserProfileCardQueryParams };
